import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiClient, ApiError, type TeamDraft } from '../lib/api';
import { useOrgSse } from '../lib/sse';

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.round(hr / 24);
  return `${d}d`;
}

export function Inbox() {
  const params = useParams();
  const orgId = decodeURIComponent(params.orgId ?? '');
  const [drafts, setDrafts] = useState<TeamDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(() => {
    let cancelled = false;
    apiClient.orgs.drafts
      .inbox(orgId)
      .then((d) => {
        if (!cancelled) setDrafts(d);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : 'Failed to load inbox');
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => refetch(), [refetch]);

  useOrgSse(orgId, { onHint: refetch });

  return (
    <main className="mx-auto h-full max-w-3xl overflow-y-auto px-6 py-6">
      <div className="mb-4 flex items-baseline gap-3">
        <h1 className="font-mono text-lg">Review inbox</h1>
        <span className="font-mono text-[12px] text-text-subtle">
          {drafts ? `${drafts.length} pending` : ''}
        </span>
        <Link
          to="/library"
          className="ml-auto text-[12px] text-text-muted hover:text-[var(--color-accent)]"
        >
          ← Library
        </Link>
      </div>
      {error && (
        <p className="rounded border bg-danger-soft p-3 text-[12px] text-[var(--color-danger)]">
          {error}
        </p>
      )}
      {drafts && drafts.length === 0 && (
        <p className="rounded border bg-surface p-4 text-[12px] text-text-subtle">
          Nothing waiting for review. New suggestions from contributors will
          appear here.
        </p>
      )}
      <ul className="space-y-2">
        {drafts?.map((draft) => (
          <li key={draft.id}>
            <Link
              to={`/orgs/${encodeURIComponent(orgId)}/drafts/${encodeURIComponent(draft.id)}/review`}
              className="block rounded border bg-canvas p-3 hover:border-[var(--color-accent)]"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-[13px]">
                  {draft.summary ?? '(no summary)'}
                </span>
                <span className="font-mono text-[11px] text-text-subtle">
                  {relativeTime(draft.submittedAt)}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-3 text-[11px] text-text-subtle">
                <span>by {draft.contributorUserId}</span>
                <span>·</span>
                <span className="truncate font-mono">{draft.skillId}</span>
              </div>
              {draft.note && (
                <p className="mt-2 text-[12px] text-text-muted">
                  &ldquo;{draft.note}&rdquo;
                </p>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
